import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import {
    Badge,
    HeaderActions,
    Heading,
    LoadingProgress,
    Panel,
    Table,
    TableFooter,
    TableToolbar,
    Text,
} from "@/ui";
import Content from "../../base/layout/content/Content";
import useMeta from "../../base/hooks/useMeta.js";
import {
    QUERY_SYSTEM_APP_LIST,
    MUTATION_REMOVE_SYSTEM_APP,
} from "../../apollo/module-operations/app";
import routes from "../../config/routes/routes";
import useRowActions from "../../base/hooks/table/useRowActions";
import { PlusIcon, RowActionsMenu, storeTableUiStyles } from "../store/shared";
import styles from "../store/GeneralsForm.module.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const Apps = () => {
    const { user } = useMeta();
    const history = useHistory();

    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { loading, data, refetch, networkStatus } = useQuery(
        QUERY_SYSTEM_APP_LIST,
        {
            skip: user.loading || !user.accountId || !user.storeId,
            notifyOnNetworkStatusChange: true,
            fetchPolicy: "network-only",
        },
    );

    const { confirm, update } = useRowActions(
        [
            {
                query: QUERY_SYSTEM_APP_LIST,
            },
        ],
        MUTATION_REMOVE_SYSTEM_APP,
        routes.APP_DETAIL.route,
        "App",
    );

    const apps = useMemo(() => (data && data.listApps) || [], [data]);

    const categoryOptions = useMemo(() => {
        const values = Array.from(
            new Set(
                apps.map(row => (row.category || "").trim()).filter(Boolean),
            ),
        ).sort((a, b) => a.localeCompare(b));
        return values;
    }, [apps]);

    const clearFilters = useCallback(() => {
        setCategoryFilter(null);
        setSearch("");
        setPage(1);
    }, []);

    const categoryMenuItems = useMemo(
        () => [
            {
                id: "all-categories",
                label: "All categories",
                onSelect: () => {
                    setCategoryFilter(null);
                    setPage(1);
                },
            },
            ...categoryOptions.map(category => ({
                id: `category-${category}`,
                label: category,
                onSelect: () => {
                    setCategoryFilter(category);
                    setPage(1);
                },
            })),
        ],
        [categoryOptions],
    );

    const toolbarFilters = useMemo(
        () => [
            {
                id: "f-category",
                label: categoryFilter
                    ? `Category · ${categoryFilter}`
                    : "Category",
                active: Boolean(categoryFilter),
                menuItems: categoryFilter ? undefined : categoryMenuItems,
                onRemove: categoryFilter
                    ? () => {
                          setCategoryFilter(null);
                          setPage(1);
                      }
                    : undefined,
            },
        ],
        [categoryFilter, categoryMenuItems],
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        return apps.filter(row => {
            if (categoryFilter && row.category !== categoryFilter) {
                return false;
            }
            if (!q) return true;

            const haystack = [row.name, row.handler, row.category]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(q);
        });
    }, [apps, search, categoryFilter]);

    const hasActiveFilters = Boolean(search.trim()) || Boolean(categoryFilter);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * pageSize;
    const pageRows = useMemo(
        () => filtered.slice(pageStart, pageStart + pageSize),
        [filtered, pageStart, pageSize],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            label={row.name || row.handler || "app"}
                            onUpdate={() => update(row.id)}
                        />
                    </span>
                ),
            },
            {
                id: "name",
                header: "Name",
                minWidth: 160,
                renderCell: row => row.name || "",
            },
            {
                id: "handler",
                header: "Handler",
                minWidth: 140,
                renderCell: row => row.handler || "",
            },
            {
                id: "category",
                header: "Category",
                minWidth: 120,
                renderCell: row => row.category || "",
            },
            {
                id: "status",
                header: "Status",
                minWidth: 90,
                renderCell: row => (
                    <Badge variant={row.status ? "primary" : "neutral"}>
                        {row.status ? "Yes" : "No"}
                    </Badge>
                ),
            },
        ],
        [],
    );

    const _loading = loading || networkStatus === 4;

    const headerActions = [
        {
            id: "create",
            label: "Add New",
            icon: <PlusIcon />,
            onClick: () => {
                history.push(routes.APP_CREATE.route);
            },
        },
    ];

    return (
        <>
            <Content>
                <Panel
                    className={styles.root}
                    header={
                        <div className={styles.headerBar}>
                            <div className={styles.headerText}>
                                <Heading level={4}>Information</Heading>
                                <Text tone="secondary" variant="body-md">
                                    Select any App for more details
                                </Text>
                            </div>
                            <HeaderActions
                                actions={headerActions}
                                aria-label="Apps actions"
                            />
                        </div>
                    }
                    showHeaderDivider
                >
                    {_loading ? (
                        <div className={styles.loading}>
                            <LoadingProgress
                                indeterminate
                                indicator="circular"
                                size="sm"
                                title="Loading"
                            />
                        </div>
                    ) : (
                        <>
                            <TableToolbar
                                count={filtered.length}
                                filters={toolbarFilters}
                                label="Apps"
                                mode={
                                    hasActiveFilters
                                        ? "active-filters"
                                        : "filterable"
                                }
                                onClearAllFilters={clearFilters}
                                onRefresh={() => refetch()}
                                onSearchChange={value => {
                                    setSearch(value);
                                    setPage(1);
                                }}
                                searchable
                                searchValue={search}
                                showMoreFilters={false}
                                updatedAtLabel=""
                            />
                            <Table
                                columns={columns}
                                getRowId={row => String(row.id)}
                                rows={pageRows}
                            />
                            <TableFooter
                                onPageChange={setPage}
                                onPageSizeChange={size => {
                                    setPageSize(size);
                                    setPage(1);
                                }}
                                page={currentPage}
                                pageSize={pageSize}
                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                                totalPages={totalPages}
                                totalRows={filtered.length}
                            />
                        </>
                    )}
                </Panel>
            </Content>
            {confirm}
        </>
    );
};

export default Apps;
